import { reaction, runInAction, when } from "mobx";
import { initComponentStore } from "@/utils/mobx";
import { appStore } from "@/app_store";

const MOBILE_QUERY = "(max-width: 768px)";

export class AppViewStore {
    authMode: "sign-in" | "sign-up" = "sign-in";
    section: "code" | "review" | "deploy" = "code";
    isAgentOpen: boolean = false;
    isMobile: boolean = window.matchMedia(MOBILE_QUERY).matches;
    isLandscape: boolean = window.matchMedia("(orientation: landscape)").matches;

    constructor() {
        initComponentStore(this);

        when(
            () => appStore.account.settings !== null,
            () => {
                runInAction(() => {
                    this.isAgentOpen = appStore.account.settings!.ui_agent_open;
                });
            }
        );

        reaction(
            () => appStore.state.sectionRequest,
            (req) => { if (req) this.setSection(req.section); }
        );

        // When the user navigates via the browser's back/forward buttons or types a
        // hash URL directly, show a file-capable section and collapse the agent on mobile.
        reaction(
            () => appStore.state.fileUrlNavigated,
            () => {
                if (this.section !== "code" && this.section !== "review") {
                    this.setSection("code");
                } else if (this.isMobile && this.isAgentOpen) {
                    this.closeAgent();
                }
            }
        );

        const mq = window.matchMedia(MOBILE_QUERY);
        mq.addEventListener("change", (e) => {
            runInAction(() => { this.isMobile = e.matches; });
        });

        const oq = window.matchMedia("(orientation: landscape)");
        oq.addEventListener("change", (e) => {
            runInAction(() => { this.isLandscape = e.matches; });
        });
    }

    get agentSizes(): number[] {
        const s = appStore.account.settings?.ui_agent_sizes;
        return s && s.length > 0 ? s : [85, 15];
    }

    setAgentSizes(sizes: number[]) {
        appStore.account.saveSettings({ ui_agent_sizes: sizes });
    }

    setAuthMode(mode: "sign-in" | "sign-up") {
        this.authMode = mode;
    }

    setSection(section: "code" | "review" | "deploy") {
        this.section = section;
        appStore.state.setCurrentSection(section);
        if (this.isMobile && this.isAgentOpen) {
            this.closeAgent();
        }
    }

    closeAgent() {
        if (this.isMobile && this.isAgentOpen) {
            appStore.state.popAgentHistoryEntry();
        }
        this.isAgentOpen = false;
        appStore.account.saveSettings({ ui_agent_open: false });
    }

    toggleAgent() {
        this.isAgentOpen = !this.isAgentOpen;
        appStore.account.saveSettings({ ui_agent_open: this.isAgentOpen });
        if (this.isMobile) {
            if (this.isAgentOpen) {
                appStore.state.pushAgentHistoryEntry();
            } else {
                appStore.state.popAgentHistoryEntry();
            }
        }
    }
}
